import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { DataService } from './data.service';
import { EnvironmentService } from './environment.service';
import { LocalizationService } from './localization.service';

export class PGNotificationContent {
    message?:string | { [lang:string]: string };
    value?:string | { [lang:string]: string };

    link?:string;
    context?:{
        resource?:string,
        element?:string,
        data?:any
    }
}

export declare type PGNotificationType = 'user'|'system'
export declare type PGNotificationCategory = 'success'|'warning'|'danger'|'info'

export class PGNotificationData {
    id:string = null;
    type:PGNotificationType = null;
    from:string = null;
    content:string|PGNotificationContent = null;
    status:string = null;
    created_at:string = null;
    updated_at:string = null;
    user_id:string = null;
    deadline?:string = null;

    has_children?:boolean = null;
    parent?:string = null;

    category?:PGNotificationCategory = null;
}

export class PGNotification extends PGNotificationData {
    content:PGNotificationContent = null;
    time:number = null;
    
    constructor(fromData?:PGNotificationData, public local?:boolean, public duration?:number) {
        super();

        if(fromData != null) {
            for(let i in fromData) {
                if(typeof this[i] != 'undefined') {
                    if(i == 'content' && typeof fromData[i] == 'string') {
                        let _content = fromData[i] as string;

                        try {
                            this.content = JSON.parse(_content);
                        }
                        catch(ex) {
                        }
                    }
                    else {
                        this[i] = fromData[i]
                    }
                }
            }

            if(this.type == 'system') this.category = 'danger';
        }

        if(this.duration != null) this.time = Date.now();
    }
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService { // TODO: cambiare nome in NotificationService?
    unreadCount:number|string = 0;

    notifications:Array<PGNotification> = []
    onNotification = new Subject<{ operation: 'add'|'del', notification: PGNotification }>()

    constructor(private dataService:DataService, private localizationService:LocalizationService, private environmentService:EnvironmentService, private authService:AuthService) { 

    }

    isInitialized = false;

    init() {
        return new Promise<void>((resolve, reject) =>{            
            this._loadNotificationData()

            this.authService.statusChange.subscribe(() => {
                this._loadNotificationData();
            })

            this._handleApplicationStatus();

            this.environmentService.applicationStatusChange.subscribe((data) => {
                this._handleApplicationStatus();
            })

            this.isInitialized = true;
            resolve();
        })
    }

    private _checkIntervalTime = 60000
    private _checkTimeout = null;

    private _pageLimit = 10;

    private _loadNotificationData() {
        clearTimeout(this._checkTimeout)

        this.unreadCount = 0;

        let _nextCheckDelay = this._checkIntervalTime;

        if(this.authService.isLoggedIn()) {

            this.dataService.getNotifications({ limit: this._pageLimit + 1, 
                filter: [{
                    field: 'status',
                    operator: '!=',
                    value: ['read']
                }, {
                    field: 'type',
                    operator: '!=',
                    value: ['system']
                }] 
            }, null, null, true).subscribe((data) => {
                for(let i = data.length - 1; i >= 0; i--) {
                    let _duration:number = null;
                    
                    if(data[i].deadline != null) {
                        let _deadlineDate = new Date(data[i].deadline);
                        _duration = _deadlineDate.getTime() - Date.now()
                    }

                    if(_duration < _nextCheckDelay) {
                        _nextCheckDelay = _duration;
                    }
                }

                if(data.length > this._pageLimit) {
                    this.unreadCount = this._pageLimit + '+'
                }
                else {
                    this.unreadCount = data.length;
                }
            })
        }

        this._checkTimeout = setTimeout(() => {
            this._loadNotificationData();
        }, _nextCheckDelay)
    }

    private _lastMaintenanceString = null;

    private _handleApplicationStatus() {
        let _maintenanceString = null;
        if(this.environmentService.applicationStatus.maintenance != null) _maintenanceString = JSON.stringify(this.environmentService.applicationStatus.maintenance)
        
        if(this._lastMaintenanceString != _maintenanceString) {
            this._lastMaintenanceString = _maintenanceString;

            if(this.environmentService.applicationStatus.maintenance != null && this.environmentService.applicationStatus.maintenance.from != null) {

                let _from = this.environmentService.applicationStatus.maintenance.from;

                let _data:any = {
                    from: _from + '|datetime',
                    from_date: _from + '|date',
                    from_time: _from + '|time',
                }

                let _to = this.environmentService.applicationStatus.maintenance.to;

                if(_to != null) {
                    _data.to = _to + '|datetime'
                    _data.to_date = _to + '|date'
                    _data.to_time = _to + '|time'
                }

                this.addLocalNotification({
                    message: 'general.scheduled-maintenance',
                    context: { 
                        data: _data
                    },
                }, 'danger', 'MAINTENANCE', 'MAINTENANCE')
            }
            else {
                this.deleteLocalNotification('MAINTENANCE')
            }
        }
    }

    private _handleNotificationData(data:PGNotificationData, local?:boolean, duration?:number) {
        let _notification = new PGNotification(data, local, duration);

        if(_notification.content != null && _notification.content.context != null && _notification.content.context.data == null && _notification.content.context.resource != null && _notification.content.context.element != null) {
            this.dataService.getElementData(_notification.content.context.resource, _notification.content.context.element).subscribe((data) => {
                _notification.content.context.data = data;
                this._dispatchNotification(_notification)
            })
        }
        else {
            this._dispatchNotification(_notification)
        }

        if(duration != null) {
            setTimeout(() => {
                this.deleteLocalNotification(data.id)
            }, duration)
        }
    }

    private _dispatchNotification(notification:PGNotification) {
        let _wasIn = false;

        for(let i = 0; i < this.notifications.length; i++) {
            if(this.notifications[i].id == notification.id) {
                this.notifications[i] = notification;
                _wasIn = true;
                break;
            }
        }

        if(!_wasIn) {
            this.notifications.unshift(notification)
            this.onNotification.next({ operation: 'add', notification: notification })
        }
    }

    deleteLocalNotification(id:string) {
        for(let j = this.notifications.length - 1; j >= 0; j--) {
            if(this.notifications[j].local && this.notifications[j].id == id) {
                let _removed = this.notifications.splice(j, 1);
                this.onNotification.next({ operation: 'del', notification: _removed[0] })
                break;
            }
        }
    }

    addLocalNotification(content:PGNotificationContent|string, category?:PGNotificationCategory, from?:string, id?:string, duration?:number) {
        if(id == null) id = Math.random().toString();

        if(typeof content == 'string') {
            content = {
                message: content
            }
        }

        this._handleNotificationData({
            id: 'LOCAL_' + id,
            from: from,
            type: null,
            category: category,
            status: null,
            content: content,
            created_at: null,
            updated_at: null,
            user_id: null,
        }, true, duration)
    }

    markAsRead(list:string|Array<string>) {
        if(typeof list == 'string') list = [list];

        this.dataService.postRequest('/notification/read/' + list.join(','), null).subscribe(() => {
            this._loadNotificationData()
        }, () => {})
    }

    getLocalizedNotificationContent(notification:PGNotification) {
        if(typeof notification.content == 'object') {
            if(notification.content != null) {
                let _value = this.localizationService.getTranslatedValue(notification.content.message || notification.content.value);
    
                let _contextData = null;
                if(notification.content.context != null) _contextData = notification.content.context.data;
    
                return this.localizationService.translate(_value, _contextData, true);
            }
        }
        else if(typeof notification.content == 'string') {
            return this.localizationService.translate(notification.content);
        }
    }
}
